/**
 * StockIssueStatusBadge — สถานะใบเบิก-ยืมอุปกรณ์
 *
 * 5 สถานะตาม backend `StockIssueStatus`:
 *   ISSUED (เบิกออกแล้ว) · PARTIALLY_RETURNED (คืนบางส่วน) · RETURNED (คืนครบแล้ว) ·
 *   WRITTEN_OFF (ตัดจำหน่าย) · CANCELLED (ยกเลิก)
 *
 * ใบที่ `is_returnable = false` (เบิกขาด/สิ้นเปลือง) คงสถานะ ISSUED ตลอด และไม่โผล่ในหน้าของค้างคืน
 * — จึงมี prop `isReturnable` ให้เขียนป้ายเป็น "เบิกขาด" แทน "เบิกออกแล้ว" ไม่ให้ผู้ใช้เข้าใจผิดว่าค้างคืน
 */
import React from 'react';
import { Badge } from '../ui/StatusBadge';
import { StockIssueStatus, statusLabel } from '../../services/stockIssueService';

type BadgeVariant = NonNullable<React.ComponentProps<typeof Badge>['variant']>;

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  ISSUED: 'warning',
  PARTIALLY_RETURNED: 'info',
  RETURNED: 'success',
  WRITTEN_OFF: 'danger',
  CANCELLED: 'secondary',
};

interface StockIssueStatusBadgeProps {
  status: StockIssueStatus | string;
  /** `status_label` ที่ backend ส่งมากับแถว — ใช้ก่อนเสมอ (แหล่งความจริงของคำไทย) */
  statusLabelText?: string | null;
  /** false = เบิกขาด (ไม่ต้องคืน) → ป้ายของสถานะ ISSUED เปลี่ยนคำ */
  isReturnable?: boolean;
  /** เลยกำหนดคืน (backend คำนวณให้) — เพิ่มป้ายแดงต่อท้าย */
  isOverdue?: boolean;
  daysOverdue?: number | null;
  size?: NonNullable<React.ComponentProps<typeof Badge>['size']>;
  className?: string;
}

const StockIssueStatusBadge: React.FC<StockIssueStatusBadgeProps> = ({
  status,
  statusLabelText,
  isReturnable,
  isOverdue = false,
  daysOverdue,
  size = 'sm',
  className = '',
}) => {
  const label =
    status === 'ISSUED' && isReturnable === false
      ? 'เบิกขาด'
      : statusLabelText || statusLabel(status);

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <Badge variant={STATUS_VARIANT[String(status)] || 'secondary'} size={size}>
        {label}
      </Badge>
      {isOverdue && (
        <Badge variant="danger" size={size}>
          {typeof daysOverdue === 'number' && daysOverdue > 0
            ? `เลยกำหนด ${daysOverdue} วัน`
            : 'เลยกำหนดคืน'}
        </Badge>
      )}
    </span>
  );
};

export default StockIssueStatusBadge;
